import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Box } from "@mui/material";
import Header from "../Components/Header";
import StepProgress from "../Components/StepProgress";
import DigiLockerCard from "../Components/DigiLockerCard";
import CameraScreen from "../Components/CameraScreen";
import LivelinessDetection from "../Components/LivelinessDetection";
import FinalScreen from "../Components/FinalScreen";
import BotAssistant from "../Components/BotAssistant";
import PermissionCheckScreen from "../Components/PermissionCheckScreen";
import { KycUserService } from "../Services/KycUserService";
import { useDispatch } from "react-redux";
import { setSession } from "../store/reducers/sessionSlice";

function Vkyc() {
  const location = useLocation();
  const dispatch = useDispatch();
  const [activeScreen, setActiveScreen] = useState(null);
  const [kycCompleted, setKycCompleted] = useState(false);
  const [noKycData, setNoKycData] = useState(false);
  const [noApplicantId, setNoApplicantId] = useState(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const session = urlParams.get("session");
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const error = urlParams.get("error");
  
    const handleKycCheck = async (session) => {
      try {
        const response = await KycUserService.checkKycStatus(session);
        if (response?.data?.data?.kyc_status === "completed") {
          setKycCompleted(true);
          setActiveScreen("final");
        } 
        else {
          localStorage.setItem("session", session);
          setActiveScreen("permission");
       }
      } catch (error) {
        setNoKycData(true);
        setActiveScreen("final");
      }
    };
  
    if (session) {
      handleKycCheck(session);
    } else if(code && state) {
      setActiveScreen("digilocker");
    } else if(error === "access_denied") {
      setActiveScreen("digilocker");
    }else {
      setNoApplicantId(true);
      setActiveScreen("final");
    }
  }, [location.search]);
  
  // Otherwise show KYC app
  const stepByScreen = {
    digilocker: 1,
    camera: 2,
    liveliness: 3,
    final: 4,
  };
  const currentStep = stepByScreen[activeScreen] ?? 1;

  const stepLabels = [
    "Verify Aadhaar",
    "Capture PAN",
    "Face Verification",
    "Confirmation",
  ];

  const handleAuthorize = () => setActiveScreen("camera");
  const handlePanConfirmed = () => setActiveScreen("liveliness");
  const handleLivelinessComplete = () => setActiveScreen("final");

  const botVoices = {
    digilocker: process.env.PUBLIC_URL + "/botVoice/VO1.mp4",
    camera: process.env.PUBLIC_URL + "/botVoice/VO2.mp4",
    final: process.env.PUBLIC_URL + "/botVoice/VO6.mp4",
  };

  if (activeScreen === "permission") {
    return (
      <Box
        sx={{
          width: "100%",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          bgcolor: "#fafafa",
        }}
      >
        <PermissionCheckScreen
          onPermissionsGranted={() => setActiveScreen("digilocker")}
        />
      </Box>
    );
  }

  // 🔹 After permission granted — show rest of the flow
  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        overflowX: "hidden",
        boxSizing: "border-box",
        px: 2,
        pt: 2,
      }}
    >
      <Header />

      {!kycCompleted && !noKycData && !noApplicantId && activeScreen !== "final" && (
        <StepProgress
          key={activeScreen}
          currentStep={currentStep}
          totalSteps={4}
          stepLabels={stepLabels}
        />
      )}

      <Box
        sx={{
          width: "100%",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          bgcolor: "#fafafa",
        }}
      >
        {activeScreen === "digilocker" && (
          <DigiLockerCard onAuthorize={handleAuthorize} />
        )}
        {activeScreen === "camera" && (
          <CameraScreen onConfirm={handlePanConfirmed} />
        )}
        {activeScreen === "liveliness" && (
          <LivelinessDetection onComplete={handleLivelinessComplete} />
        )}
        {activeScreen === "final" && <FinalScreen 
         alreadyCompleted={kycCompleted} noKycData={noKycData} noApplicantId={noApplicantId}/>}
      </Box>

      <BotAssistant voiceFile={botVoices[activeScreen]} show={true} />
    </Box>
  );
}

export default Vkyc;
